// Chargebee.js lifecycle, wrapped so a Stimulus controller never touches
// window.Chargebee directly. One factory owns the whole sequence the embedded
// payment component needs -- init, components(), create('payment'), mount,
// validate, confirm, tearDown -- because Chargebee.js keeps the instance and
// the component bag as singletons that refuse re-registration: a second init()
// without a tearDown() leaves a dead component behind, and a failed confirm()
// can only be cleared by remounting with a fresh intent.
//
// Shared by the proposal payment section (sales_payment_controller) and, in
// a follow-up, the registration wizard's payment step
// (controllers/registration/chargebee_payment_controller), which still carries
// its own copy of this sequence. ISSUE-3822.
//
// declineMessageFor() translates a gateway failure into copy a customer can act
// on: the component's own errors read like log lines ("Your card was
// declined.; code: card_declined;") and the lost/stolen codes leak details the
// cardholder should not read off a checkout page.

const DECLINE_MESSAGES = {
  card_declined: 'Your card was declined. Please try a different card, or pay by bank transfer (Direct Debit) instead.',
  do_not_honor: 'Your card was declined by your bank. Please contact your bank or try a different payment method.',
  transaction_not_allowed: 'Your card was declined by your bank. Please contact your bank or try a different payment method.',
  lost_card: 'Your card was declined. Please try a different card, or pay by bank transfer (Direct Debit) instead.',
  stolen_card: 'Your card was declined. Please try a different card, or pay by bank transfer (Direct Debit) instead.',
  insufficient_funds: 'Your card was declined due to insufficient funds. Please try a different payment method.',
  expired_card: 'This card has expired. Please use a different card.',
  incorrect_cvc: 'The security code (CVV) does not match your card. Please check it and try again.',
  invalid_cvc: 'The security code (CVV) does not match your card. Please check it and try again.',
  incorrect_number: 'The card number appears to be incorrect. Please check it and try again.',
  invalid_number: 'The card number appears to be incorrect. Please check it and try again.',
  incorrect_zip: 'The billing ZIP code does not match your card. Please check it and try again.',
  processing_error: 'Something went wrong while processing your payment method. Please try again in a moment.'
}

const GENERIC_DECLINE_MESSAGE =
  'We could not verify your payment method. Please check the details and try again, or use a different payment method.'

const EXPIRED_INTENT_MESSAGE =
  'The payment form timed out and has been refreshed - please re-enter your details and try again.'

// The component surfaces the gateway code either as error.code or embedded in
// the message ("...; code: card_declined;").
function declineCode(error) {
  if (error?.code) return error.code

  const match = (error?.message || '').match(/code:\s*([a-z0-9_]+)/i)
  return match ? match[1].toLowerCase() : null
}

// Known decline codes are checked before the intent-expiry heuristic:
// expired_card would otherwise match /expired/ and read as a timed-out form.
export function declineMessageFor(error) {
  const known = DECLINE_MESSAGES[declineCode(error)]
  if (known) return known
  if (/expired|consumed/i.test(error?.message || '')) return EXPIRED_INTENT_MESSAGE

  return GENERIC_DECLINE_MESSAGE
}

export default function createChargebeePayment({
  site,
  publishableKey,
  mountSelector,
  allowed = ['card', 'direct_debit'],
  onSuccess,
  onError,
  onPaymentMethodChange
}) {
  let component = null

  const closeComponent = () => {
    if (!component) return

    try {
      if (typeof component.close === 'function') {
        component.close()
      } else if (typeof component.unmount === 'function') {
        component.unmount()
      }
    } catch (error) {
      console.warn('[Chargebee] Error closing payment component:', error)
    }
    component = null
  }

  const tearDown = () => {
    closeComponent()
    if (window.Chargebee && typeof window.Chargebee.tearDown === 'function') {
      try {
        window.Chargebee.tearDown()
      } catch (error) {
        console.warn('[Chargebee] Error tearing down Chargebee:', error)
      }
    }
  }

  return {
    async mount(paymentIntent) {
      tearDown()
      // ACH first: it carries no processing fee.
      const defaultPaymentMethodType = allowed.includes('direct_debit') ? 'direct_debit' : allowed[0]
      const cbInstance = window.Chargebee.init({ site, publishableKey })
      const components = cbInstance.components({ locale: 'en', style: { theme: { appearance: 'light' } } })

      component = components.create(
        'payment',
        { paymentIntent, layout: { type: 'panel' }, defaultPaymentMethodType, paymentMethods: { allowed } },
        { onError, onSuccess, onPaymentMethodChange }
      )
      await component.mount(mountSelector)
      // Chargebee does not fire onPaymentMethodChange for the type it opens on,
      // so the caller would never learn which one that is.
      if (onPaymentMethodChange) onPaymentMethodChange(defaultPaymentMethodType)
      return component
    },

    // confirm() does NOT validate -- calling it with empty or invalid fields
    // fires a doomed tokenization request whose raw gateway error the component
    // renders verbatim. validate() marks the offending fields inline instead
    // and tells the caller to stop.
    async validate() {
      return component ? component.validate() : false
    },

    async confirm() {
      if (component) await component.confirm()
    },

    tearDown
  }
}
